// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isObject from 'lodash/isObject';
import get from 'lodash/get';
// Components
import ChartOfViews from '../../../containers/ChartOfViews/ChartOfViews';
import PropertyCell from '../../PropertyCell/PropertyCell';
import DeletePropertyModal from '../../DeletePropertyModal/DeletePropertyModal';
import OMRegularManageCellMobile from '../../OMComponents/OMRegularManageCellMobile/OMRegularManageCellMobile';
import OMMultiPinCell from '../OMPropertyCells/OMMultiPinCell';
// Helpers
import {
  localesWithEnglishAddress,
  language,
  getSupportEmail,
  analyticsEventAction,
  projectStatuses,
  ENTITY_TYPES,
  QUERY_PARAMS,
  imageModifierTypes
} from '../../../constants';
import { formatPropertyTitle } from '../../../utils/formatSEOData';
import formatMessage from '../../../utils/formatMessage';
import { checkIsNewProperty, checkIsPublishedToday } from '../../../utils/checkIsNewProperty';
import getLocalizedData from '../../../utils/getLocalizedData';
import modifyImageLink from '../../../utils/modifyImageLink';
// Styles
import * as styles from './OMPropertiesList.scss';


export default class OMPropertiesList extends Component {
  state = {
    deletePropertyId: null,
    deleteModalIsOpen: false
  };

  getCellComponent = () => {
    const { className } = this.props;

    switch (className) {
      case 'inManagePropertiesMobile':
        return OMRegularManageCellMobile;
      case 'multiPinList':
        return OMMultiPinCell;
      default:
        return PropertyCell;
    }
  };

  createList = (properties) => {
    const {
      locale,
      translate,
      propertiesAnalytics,
      editable,
      updateAutoJumpToTopActivity,
      updatePropertyHighlighting,
      updateTopPromotionActivity,
      disabledPromotionAction,
      openMessageModal,
      isCanManagePromotions,
      location
    } = this.props;
    const query = get(location, 'query');
    const managedPropertyId = query ? query[QUERY_PARAMS.MANAGED_PROPERTY] : undefined;

    const CellComponent = this.getCellComponent();

    return properties.map((property) => {
      if (managedPropertyId === property.id) {
        property.thumbnail = modifyImageLink(property.thumbnail, imageModifierTypes.NOT_MODIFIED);
      }
      const isLand = (property.property_type === 'land'); // temp patch
      const englishAddressRequired = localesWithEnglishAddress.includes(locale);
      const address = englishAddressRequired
        ? property.address.en
        : (property.address[locale] || property.address[language.default]);

      const houseNumber = (address.house_number) ? ` ${address.house_number},` : '';
      const stringAddress = `${address.street_name} ${houseNumber}`;

      const cityName = address.city_name;
      const isFavorite = (this.props.favoriteProperties
        && this.props.favoriteProperties.indexOf(property.id) !== -1);

      const additionalInfo = property.additional_info || {};
      const optionType = this.props.translate('propertyDetails', 'propertyType', property.search_option);
      const propertyType = this.props.translate('propertyTypes', property.property_type)();

      const optionAndPropertyTypesTitle = (this.props.editable)
        ? `${propertyType} ${optionType}`
        : '';
      const floor = isLand ? undefined : get(property, 'additional_info.floor.on_the');
      const rooms = isLand ? undefined : additionalInfo.rooms;

      const { lat, lon } = property.address.location;
      const thumbnail =
        managedPropertyId === property.id
        ? modifyImageLink(property.thumbnail, imageModifierTypes.NOT_MODIFIED) || null
        : property.thumbnail || null;

      const isNewProperty = (!editable && property.created_at && this.props.todayDate)
        && checkIsNewProperty(property.created_at, this.props.todayDate);

      const isPublishedToday = (property.created_at && this.props.todayDate)
        && checkIsPublishedToday(property.created_at, this.props.todayDate);

      const canMoveToTop = (
        !property.is_autojump_enabled
        && property.move_to_top
        && !property.is_promoted
        && !property.is_top_promoted
      );
      const canSecondJump = !property.is_autojump_enabled
        && !isPublishedToday
        && !canMoveToTop
        && !property.shared_today;

      const sharingTitle = formatPropertyTitle(
        property,
        propertyType,
        translate('propertyDetails'),
        locale
      );

      const propertyAnalyticsData = propertiesAnalytics &&
        propertiesAnalytics.data && propertiesAnalytics.data.find(item => (item.id === property.id));
      const { events } = propertyAnalyticsData || {};
      const propertyViewEvent = events && events.find(item => (item.action === analyticsEventAction.VIEW));
      const { count: propertyViews } = propertyViewEvent || {};

      const propertyAnalyticsFavouritesData = propertiesAnalytics && propertiesAnalytics.favoritesData
        && propertiesAnalytics.favoritesData.find(item => (item.id === property.id));
      const { events: favoritesEvents } = propertyAnalyticsFavouritesData || {};
      const propertyFavoritesEvent = favoritesEvents
        && favoritesEvents.find(item => (item.action === analyticsEventAction.FAVOURITES));
      const { count: propertyFavorites } = propertyFavoritesEvent || {};

      const propertyAnalyticsNumberData = propertiesAnalytics && propertiesAnalytics.ShowNumberData
        && propertiesAnalytics.ShowNumberData.find(item => (item.id === property.id));
      const { events: numberEvents } = propertyAnalyticsNumberData || {};
      const propertyNumberEvent = numberEvents
        && numberEvents.find(item => (item.action === analyticsEventAction.SHOW_NUMBER));
      const { count: propertyNumber } = propertyNumberEvent || {};

      const createdAt = property.created_at;
      const openJumpToTopModal = () => this.props.openJumpToTopModal(property, true);
      const name = getLocalizedData(property.name, locale);
      const price = property.price || property.min_property_price;
      const statusLocaliseData = projectStatuses.find(item => (item.name === property.status)) || {};
      const status = getLocalizedData(statusLocaliseData.localisation, locale);
      const image = (property.entityType === ENTITY_TYPES.PROJECT) ? get(property, 'images[0]', null) : null;

      return (
        <div key={property.id}>
          <CellComponent
            userProperties={this.props.userProperties}
            property={property}
            id={property.id}
            slug={property.slug}
            locale={locale}
            photo={image || thumbnail}
            price={price}
            currency={property.currency}
            prices={property.prices}
            overrideCurrency={this.props.overrideCurrency}
            imageModifier={managedPropertyId === property.id ? imageModifierTypes.NOT_MODIFIED : undefined}
            addressData={property.address}
            address={stringAddress}
            city={cityName}
            isCanManagePromotions={isCanManagePromotions}
            updateAutoJumpToTopActivity={updateAutoJumpToTopActivity}
            updatePropertyHighlighting={updatePropertyHighlighting}
            updateTopPromotionActivity={updateTopPromotionActivity}
            disabledPromotionAction={disabledPromotionAction}
            openMessageModal={openMessageModal}
            rooms={rooms}
            parkingSpots={additionalInfo.parking}
            openDetailsPopup={this.props.openDetailsPopup}
            area={isObject(additionalInfo.area) ? additionalInfo.area.base : additionalInfo.area} // for migration purposes
            floor={floor}
            description={property.description}
            optionAndPropertyTypes={optionAndPropertyTypesTitle}
            parkingIconTitle={this.props.translate('iconTitles', 'parkingIcon')}
            roomsIconTitle={this.props.translate('iconTitles', 'roomsIcon')}
            squareIconTitle={this.props.translate('iconTitles', 'squareIcon')}
            floorIconTitle={this.props.translate('iconTitles', 'floorIcon')}
            groundFloorTitle={this.props.translate('propertyDetails', 'details', 'groundFloorShortcut')}
            className={this.props.className}
            editButtonText={this.props.translate('edit')}
            copyLinkText={this.props.translate('copyLink')}
            shareButtonText={this.props.translate('share')}
            openDetailsButtonText={this.props.translate('openDetails')}
            deleteButtonText={this.props.translate('propertyDetails', 'delete')}
            activeButtonText={this.props.translate('propertyDetails', 'activate')}
            inactiveButtonText={this.props.translate('propertyDetails', 'deactivate')}
            makeActiveText={this.props.translate('propertyDetails', 'makeActive')}
            makeInactiveText={this.props.translate('propertyDetails', 'makeInactive')}
            jumpToTopText={this.props.translate('propertyDetails', 'jumpToTop')}
            jumpToTopTooltipText={this.props.translate('propertyDetails', 'jumpToTopTooltip')}
            propertyViewsText={this.props.translate('propertyDetails', 'views')}
            upgradePackageButtonText={this.props.translate('propertyDetails', 'upgradePackage')}
            bathrooms={property.bathrooms}
            editProperty={this.props.editProperty}
            editable={this.props.editable}
            isMobile={this.props.isMobile}
            isActive={property.is_active}
            updatePropertyActivity={this.props.updatePropertyActivity}
            movePropertyToTop={this.props.movePropertyToTop}
            canMoveToTop={canMoveToTop}
            canSecondJump={canSecondJump}
            showCantMakeActiveModal={this.showCantMakeActiveModal}
            showDeleteModal={this.showDeleteModal}
            propertyType={propertyType || name.result}
            toggleFavorite={this.props.toggleFavorite}
            isFavorite={isFavorite}
            mapPropertyCell={this.props.mapPropertyCell}
            isRtl={this.props.isRtl}
            promotedTitle={this.props.promotedTitle}
            suspended={property.is_suspended}
            suspendedTitle={this.props.suspendedTitle}
            suspendedTooltipMessage={this.props.suspendedTooltipMessage}
            sharingTitle={sharingTitle}
            suppressLoading={this.props.suppressLoading}
            isNewProperty={isNewProperty}
            isModalShareOpen={this.props.isModalShareOpen}
            hideModalShare={this.props.hideModalShare}
            openModalShare={this.props.openModalShare}
            openJumpToTopModal={openJumpToTopModal}
            newPropertyLabel={this.props.translate('iconTitles', 'newPropertyLabel')()}
            newPropertyTitle={this.props.translate('iconTitles', 'newPropertyTitle')()}
            autoDeactivatedProperty={this.props.autoDeactivatedProperty}
            openChartOfViews={this.props.openChartOfViews}
            propertyViews={propertyViews}
            propertyFavorites={propertyFavorites}
            propertyNumber={propertyNumber}
            loadSinglePropertyAnalytics={this.props.loadSinglePropertyAnalytics}
            createdAt={createdAt}
            translate={this.props.translate}
            promoted={property.is_promoted}
            hasVRLink={!!property.vr_link}
            autoJumpEnabled={property.is_autojump_enabled}
            isTopPromotedProperty={property.is_top_promoted}
            modalShareId={this.props.modalShareId}
            propertyEntityType={property.entityType}
            status={status.result}
            searchOption={property.search_option}
            openUpgradePackageModal={this.props.openUpgradePackageModal}
            isNative={this.props.isNative}
          />
        </div>
      );
    });
  };

  showCantMakeActiveModal = () => {
    const message = formatMessage(this.props.translate('propertyDetails', 'cantMakeActive'), [
      getSupportEmail(),
      getSupportEmail()
    ]);
    this.props.openMessageModal({
      message: (
        <div className="contentMessage">
          <p className={styles.modalMessageWrapper} dangerouslySetInnerHTML={{ __html: message }} />
        </div>
      )
    });
  };

  showDeleteModal = (id) => {
    this.setState({
      deletePropertyId: id,
      deleteModalIsOpen: true
    });
  };

  hideDeleteModal = () => this.setState({
    deletePropertyId: null,
    deleteModalIsOpen: false
  });

  handleDeleteProperty = ({ option, answer }) => {
    console.log(option, answer);
  };

  render() {
    const { translate, propertiesAnalytics, closeChartOfViews, editable } = this.props;
    return (
      <div className={(this.props.needScroll) ? styles.scrollPropertiesList : styles.wrap}>
        {this.createList(this.props.properties)}

        {(this.props.editable && this.state.deleteModalIsOpen) ? (
          <DeletePropertyModal
            isOpen={this.state.deleteModalIsOpen}
            hideDeleteModal={this.hideDeleteModal}
            headerText={this.props.translate('propertyDetails', 'deletePropertyQuestion')}
            selectPlaceholder={this.props.translate('propertyDetails', 'choose')}
            inputPlaceholder={this.props.translate('propertyDetails', 'answer')}
            errorText={this.props.translate('propertyDetails', 'pleaseChooseAnAnswer')}
            deleteOptions={[{ label: 'Delete please', value: 'simple' }, { label: 'אחר', value: 'other' }]}
            handleSubmit={this.handleDeleteProperty}
            deleteButtonText={this.props.translate('propertyDetails', 'deleteButtonText')}
            cancelButtonText={this.props.translate('propertyDetails', 'cancelButtonText')}
          />
        ) : null}
        {editable && <ChartOfViews
          translate={translate}
          propertiesAnalytics={propertiesAnalytics}
          onRequestClose={closeChartOfViews}
          isRtl={this.props.isRtl}
        />}
      </div>
    );
  }
}

OMPropertiesList.propTypes = {
  locale: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  properties: PropTypes.array,
  translate: PropTypes.func,
  className: PropTypes.string,
  openDetailsPopup: PropTypes.func,
  editProperty: PropTypes.func,
  editable: PropTypes.bool,
  needScroll: PropTypes.bool,
  updatePropertyActivity: PropTypes.func,
  movePropertyToTop: PropTypes.func,
  favoriteProperties: PropTypes.array,
  toggleFavorite: PropTypes.func,
  openMessageModal: PropTypes.func,
  isModalShareOpen: PropTypes.bool,
  hideModalShare: PropTypes.func,
  openModalShare: PropTypes.func,
  openJumpToTopModal: PropTypes.func,
  mapPropertyCell: PropTypes.bool,
  promotedTitle: PropTypes.string,
  suspendedTitle: PropTypes.string,
  suspendedTooltipMessage: PropTypes.string,
  isRtl: PropTypes.bool,
  suppressLoading: PropTypes.func,
  todayDate: PropTypes.object,
  autoDeactivatedProperty: PropTypes.string,
  propertiesAnalytics: PropTypes.object,
  closeChartOfViews: PropTypes.func,
  openChartOfViews: PropTypes.func,
  modalShareId: PropTypes.string,
  loadSinglePropertyAnalytics: PropTypes.func,
  openUpgradePackageModal: PropTypes.func,
  userProperties: PropTypes.object,
  updateAutoJumpToTopActivity: PropTypes.func,
  updatePropertyHighlighting: PropTypes.func,
  updateTopPromotionActivity: PropTypes.func,
  disabledPromotionAction: PropTypes.func,
  isCanManagePromotions: PropTypes.bool,
  isNative: PropTypes.bool,
  location: PropTypes.string,
  overrideCurrency: PropTypes.string
};
